import React, { useState, useEffect, useCallback } from 'react';
import { FlaskConical } from 'lucide-react';
import SessionSidebar from './SessionSidebar';
import ChatView from './ChatView';
import { getSessions, createSession, getSession } from '../../services/insightLabService';
import { useAuth } from '../../stores/AuthContext';
import { trackInsightLabSessionCreated } from '../../services/analytics';

const InsightLab = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const fetchSessions = useCallback(async () => {
    try {
      const data = await getSessions();
      setSessions(data);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleSelectSession = useCallback(async (sessionId) => {
    try {
      const session = await getSession(sessionId);
      setActiveSessionId(session.id);
      setActiveSession(session);
      setMessages(session.messages || []);
    } catch (err) {
      console.error('Failed to load session:', err);
    }
  }, []);

  const handleNewSession = useCallback(async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const { session, messages: initialMessages } = await createSession();
      trackInsightLabSessionCreated();
      await fetchSessions();
      setActiveSessionId(session.id);
      setActiveSession(session);
      setMessages(initialMessages);
    } catch (err) {
      console.error('Failed to create session:', err);
    } finally {
      setIsCreating(false);
    }
  }, [isCreating, fetchSessions]);

  const handleSessionUpdate = useCallback(() => {
    // Re-fetch sessions to pick up title changes
    fetchSessions();
  }, [fetchSessions]);

  return (
    <div className="fixed inset-0 bg-background pt-16">
      <div className="flex h-full">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'w-72' : 'w-0'} flex-shrink-0 transition-all duration-200 overflow-hidden`}>
          <SessionSidebar
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSelectSession={handleSelectSession}
            onNewSession={handleNewSession}
            onSessionsChange={fetchSessions}
            isCreating={isCreating}
          />
        </div>

        {/* Main chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex-shrink-0 px-4 py-3 border-b border-border bg-card flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 rounded-lg hover:bg-accent/50 text-muted-foreground hover:text-foreground transition-colors"
              title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
            >
              <FlaskConical className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-foreground">
                {activeSession?.title || 'Insight Lab'}
              </h1>
              <p className="text-xs text-muted-foreground">
                Deep conversational coaching powered by your trading data
              </p>
            </div>
          </div>

          {/* Chat */}
          <ChatView
            session={activeSession}
            messages={messages}
            setMessages={setMessages}
            onSessionUpdate={handleSessionUpdate}
          />
        </div>
      </div>
    </div>
  );
};

export default InsightLab;
